/**
 * Auto-start (and auto-finish) a stop by where the phone is — the rules, with no React and no network
 * in them, so they can be tested (server/tests/auto-start-geofence.test.js compiles this file and runs
 * it the way day-plan-words.test.js does for the day's words).
 *
 * Michael, Nov 3 2026: "Field / My Day auto-start jobs by location … zero taps required … if multiple
 * stops' geofences overlap, only auto-start the stop that is next in route order / nearest, never two at
 * once; don't auto-complete stop A just because they walked to neighbor B unless dwell says so."
 *
 * The three things that make this safe, in one place:
 *
 *  1. **A fix the phone is not sure about is not used at all.** `usableFix` throws away a reading whose
 *     own accuracy circle is bigger than the fence (a 200 m-accurate fix "inside" a 75 m fence says
 *     nothing) and anything older than `MAX_FIX_AGE_MS`. An unused fix neither starts nor extends a
 *     dwell — it is as if the phone said nothing.
 *  2. **Dwell is time, not a count of reads.** Two reads in a row can be two seconds apart at a red
 *     light one street over. `advance` holds the moment the phone first came inside and only acts once
 *     it has been inside continuously for the dwell.
 *  3. **One stop at a time, chosen on purpose.** At 75 m two stops that share a day can both hold the
 *     phone (CLAUDE.md, measured 2026-08-12: the closest pair on one day is 104 m apart — Lone Wolf Dr
 *     and Abbey Ln are the kind of pair this is about). `pickArrival` returns at most ONE stop: the
 *     earliest in route order among those within `ORDER_TIE_METERS` of the nearest — so the route is
 *     worked in order, unless another stop is clearly closer. And nothing auto-starts at all while a
 *     stop is already running, so two stops can never be running together.
 *
 * Leaving is deliberately the mirror of arriving, not a cheaper test: the phone has to be *outside* the
 * running stop's fence continuously for `leave` seconds. Walking next door usually leaves the phone
 * inside the fence it started in, so nothing fires — which is the point.
 */

// ── What the engine is handed ────────────────────────────────────────────────

/** One GPS reading. `accuracy` is the radius in metres the browser reports; `at` is Date.now() when it arrived. */
export type Fix = { lat: number; lng: number; accuracy: number; at: number };

/**
 * A stop as the engine needs it. `order` is its place in the day (route position, or the index in the
 * list the page shows). `status` / `actual_start_time` come straight off the job row.
 */
export type GeoStop = {
  id: number;
  lat: number | null;
  lng: number | null;
  status?: string | null;
  actual_start_time?: string | null;
  order: number;
};

export type OnLeave = 'prompt' | 'complete';

/** The owner's five numbers (server/services/autoStart.js is the writer; these are its defaults). */
export type AutoStopSettings = {
  enabled: boolean;
  radius: number;    // metres
  dwell: number;     // seconds inside before the stop starts
  leave: number;     // seconds outside before the stop counts as left
  onLeave: OnLeave;
};

export const AUTO_STOP_DEFAULTS: AutoStopSettings = { enabled: true, radius: 75, dwell: 60, leave: 90, onLeave: 'prompt' };

/** Limits mirrored from server/services/autoStart.js LIMITS — a bad settings row cannot wreck a day. */
export const AUTO_STOP_LIMITS = { radius: [25, 150], dwell: [15, 600], leave: [30, 1800] } as const;

/**
 * A second stop this close to the nearest one is treated as the same place, and route order decides
 * between them. Wide enough to cover ordinary GPS wobble, narrow enough that a stop plainly further
 * down the street does not win just because it is next in the route.
 */
export const ORDER_TIE_METERS = 25;

/** A fix older than this is history — the phone has been asleep, or the page was in the background. */
export const MAX_FIX_AGE_MS = 60_000;

/** Mirrors server/services/autoStart.js settingsFrom, so what the phone does matches what the API said. */
export function readAutoStopSettings(raw: any): AutoStopSettings {
  const clamp = (v: any, lo: number, hi: number, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : fallback;
  };
  const leave = String(raw?.onLeave ?? '').trim().toLowerCase();
  return {
    enabled: raw?.enabled !== false,
    radius: clamp(raw?.radius, AUTO_STOP_LIMITS.radius[0], AUTO_STOP_LIMITS.radius[1], AUTO_STOP_DEFAULTS.radius),
    dwell: clamp(raw?.dwell, AUTO_STOP_LIMITS.dwell[0], AUTO_STOP_LIMITS.dwell[1], AUTO_STOP_DEFAULTS.dwell),
    leave: clamp(raw?.leave, AUTO_STOP_LIMITS.leave[0], AUTO_STOP_LIMITS.leave[1], AUTO_STOP_DEFAULTS.leave),
    onLeave: leave === 'complete' ? 'complete' : 'prompt',
  };
}

// ── Distance ────────────────────────────────────────────────────────────────

/** Metres between two points, on the ground (the same haversine the server and the crew timer use). */
export function metersBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** How far the phone is from a stop, or null when either end has no pin. */
export function metersTo(stop: GeoStop, fix: Fix | null): number | null {
  if (!fix || stop.lat == null || stop.lng == null) return null;
  const lat = Number(stop.lat), lng = Number(stop.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return metersBetween(fix.lat, fix.lng, lat, lng);
}

/**
 * Is this reading worth acting on? A fix whose own accuracy circle is wider than the fence cannot say
 * which side of the fence the phone is on, and a fix from five minutes ago says where it used to be.
 */
export function usableFix(fix: Fix | null | undefined, radius: number, now = Date.now()): boolean {
  if (!fix) return false;
  if (!Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return false;
  const acc = Number(fix.accuracy);
  if (!Number.isFinite(acc) || acc <= 0 || acc > radius) return false;
  if (Number.isFinite(fix.at) && now - fix.at > MAX_FIX_AGE_MS) return false;
  return true;
}

// ── Which stops are in play ─────────────────────────────────────────────────

const st = (s: GeoStop) => String(s.status || 'scheduled').toLowerCase();

/** A stop that could be started: still to do, never started, and pinned on the map. */
export function startable(s: GeoStop): boolean {
  return st(s) === 'scheduled' && !s.actual_start_time && s.lat != null && s.lng != null;
}

/** The stop whose clock is running. */
export function isRunning(s: GeoStop): boolean {
  return st(s) === 'in_progress';
}

/** The running stop of a day, or null. Two would be a data problem; the first in order is used. */
export function runningStop(stops: GeoStop[]): GeoStop | null {
  return stops.filter(isRunning).sort((a, b) => a.order - b.order)[0] || null;
}

/** Every startable stop whose fence the phone is inside, nearest first. */
export function nearbyStartable(stops: GeoStop[], fix: Fix | null, radius: number): { stop: GeoStop; meters: number }[] {
  const out: { stop: GeoStop; meters: number }[] = [];
  for (const s of stops) {
    if (!startable(s)) continue;
    const m = metersTo(s, fix);
    if (m == null || m > radius) continue;
    out.push({ stop: s, meters: m });
  }
  return out.sort((a, b) => a.meters - b.meters || a.stop.order - b.stop.order);
}

/**
 * The one stop the phone has arrived at, or null — never two.
 *
 * Fences overlap on a street like Lone Wolf Dr / Abbey Ln, so being inside two is normal. Route order
 * decides between stops that are effectively the same distance away (within ORDER_TIE_METERS of the
 * nearest); otherwise the nearest wins, because a stop 70 m off is not the one being stood on.
 * `overlapping` is every other stop whose fence the phone is also inside — the page says so.
 */
export function pickArrival(stops: GeoStop[], fix: Fix | null, settings: AutoStopSettings)
  : { stop: GeoStop; meters: number; overlapping: GeoStop[] } | null {
  // A stop already running owns the phone: nothing else may start while it does.
  if (runningStop(stops)) return null;
  const near = nearbyStartable(stops, fix, settings.radius);
  if (!near.length) return null;
  const band = near[0].meters + ORDER_TIE_METERS;
  const tied = near.filter(c => c.meters <= band);
  const chosen = tied.reduce((a, b) => (b.stop.order < a.stop.order ? b : a));
  return { stop: chosen.stop, meters: chosen.meters, overlapping: near.filter(c => c.stop.id !== chosen.stop.id).map(c => c.stop) };
}

// ── The dwell clock ─────────────────────────────────────────────────────────

export type AutoStopState = {
  /** The stop the phone is currently sitting inside, and since when. */
  candidateId: number | null;
  insideSince: number | null;
  /** The running stop the phone has been outside of, and since when. */
  awayFromId: number | null;
  outsideSince: number | null;
  /** Stops this page has already acted on, so a slow refetch cannot make it act twice. */
  startedIds: number[];
  leftIds: number[];
};

export const EMPTY_AUTO_STOP_STATE: AutoStopState = {
  candidateId: null, insideSince: null, awayFromId: null, outsideSince: null, startedIds: [], leftIds: [],
};

export type AutoStopAction =
  | { kind: 'start'; jobId: number; meters: number; overlapping: number[] }
  | { kind: 'leave'; jobId: number; mode: OnLeave; meters: number }
  | null;

/** Forget both clocks, keeping what has already been acted on. Used when the page comes back to the front. */
export function resetTimers(state: AutoStopState): AutoStopState {
  return { ...state, candidateId: null, insideSince: null, awayFromId: null, outsideSince: null };
}

/** Note that a stop has been started (or left) so it is never acted on twice. */
export function markActed(state: AutoStopState, kind: 'start' | 'leave', jobId: number): AutoStopState {
  const key = kind === 'start' ? 'startedIds' : 'leftIds';
  if (state[key].includes(jobId)) return state;
  return { ...state, [key]: [...state[key], jobId] };
}

/**
 * One GPS reading in, the next state and at most one action out. Pure: the caller performs the action
 * and calls `markActed`, so a failed API call can be retried on the next fix.
 */
export function advance(
  state: AutoStopState,
  { stops, fix, now, settings }: { stops: GeoStop[]; fix: Fix | null; now: number; settings: AutoStopSettings },
): { state: AutoStopState; action: AutoStopAction } {
  if (!settings.enabled) return { state: resetTimers(state), action: null };
  // A reading the phone is not sure about is not a reading: neither clock moves, nothing is decided.
  if (!usableFix(fix, settings.radius, now)) return { state, action: null };

  const running = runningStop(stops);

  // ── A stop is running: the only question is whether the phone has left it ──
  if (running) {
    const away = metersTo(running, fix);
    const outside = away == null ? false : away > settings.radius;
    // No pin on the running stop, or the phone is back inside: the leave clock is dropped.
    if (!outside) {
      return { state: { ...state, candidateId: null, insideSince: null, awayFromId: null, outsideSince: null }, action: null };
    }
    const next: AutoStopState = state.awayFromId === running.id && state.outsideSince != null
      ? state
      : { ...state, awayFromId: running.id, outsideSince: now, candidateId: null, insideSince: null };
    const since = next.outsideSince as number;
    if (!next.leftIds.includes(running.id) && now - since >= settings.leave * 1000) {
      return { state: next, action: { kind: 'leave', jobId: running.id, mode: settings.onLeave, meters: away as number } };
    }
    return { state: next, action: null };
  }

  // ── Nothing running: has the phone been standing at one stop long enough? ──
  const arrival = pickArrival(stops, fix, settings);
  if (!arrival) {
    return { state: { ...state, candidateId: null, insideSince: null, awayFromId: null, outsideSince: null }, action: null };
  }
  const id = arrival.stop.id;
  const next: AutoStopState = state.candidateId === id && state.insideSince != null
    ? { ...state, awayFromId: null, outsideSince: null }
    : { ...state, candidateId: id, insideSince: now, awayFromId: null, outsideSince: null };
  const since = next.insideSince as number;
  if (!next.startedIds.includes(id) && now - since >= settings.dwell * 1000) {
    return { state: next, action: { kind: 'start', jobId: id, meters: arrival.meters, overlapping: arrival.overlapping.map(s => s.id) } };
  }
  return { state: next, action: null };
}

// ── Words for the page ──────────────────────────────────────────────────────

/** "watching — 3 min to go", "here · starting in 40 s", "no GPS yet". One line, no jargon. */
export function watchWords(
  { state, fix, stops, settings, now }: { state: AutoStopState; fix: Fix | null; stops: GeoStop[]; settings: AutoStopSettings; now: number },
): string {
  if (!settings.enabled) return 'Auto-start is off';
  if (!fix) return 'Waiting for GPS';
  if (!usableFix(fix, settings.radius, now)) return `GPS is only good to ${Math.round(Number(fix.accuracy) || 0)} m — too rough to use`;
  const running = runningStop(stops);
  if (running) {
    const away = metersTo(running, fix);
    if (state.outsideSince != null && state.awayFromId === running.id) {
      const left = Math.max(0, Math.ceil((state.outsideSince + settings.leave * 1000 - now) / 1000));
      return left > 0 ? `Left the yard — asking about it in ${left} s` : 'Left the yard';
    }
    return away == null ? 'On a stop' : `On this stop · ${Math.round(away)} m from the pin`;
  }
  if (state.insideSince != null && state.candidateId != null) {
    const left = Math.max(0, Math.ceil((state.insideSince + settings.dwell * 1000 - now) / 1000));
    return left > 0 ? `Here — starting in ${left} s` : 'Starting…';
  }
  const near = stops.map(s => metersTo(s, fix)).filter((m): m is number => m != null).sort((a, b) => a - b)[0];
  return near == null ? 'Watching — no stop is pinned on the map' : `Watching · nearest stop ${Math.round(near)} m away`;
}
